import * as XLSX from "xlsx";

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function downloadBlob(blob, filename) {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function csvCell(value) {
    return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function buildSectionRows(section) {
    const rows = [];

    if (section.title) {
        rows.push([section.title]);
    }

    rows.push(section.columns);
    section.rows.forEach((row) => rows.push(row));

    if (section.footerRows?.length) {
        section.footerRows.forEach((row) => rows.push(row));
    }

    return rows;
}

function buildPrintableTable(section) {
    const thead = `
        <thead>
            <tr>${section.columns.map((column) => `<th>${escapeHtml(column)}</th>`).join("")}</tr>
        </thead>
    `;

    const tbody = `
        <tbody>
            ${section.rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")}
        </tbody>
    `;

    const tfoot = section.footerRows?.length ? `
        <tfoot>
            ${section.footerRows.map((row) => `<tr>${row.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>`).join("")}
        </tfoot>
    ` : "";

    return `
        <section class="report-section">
            <h2>${escapeHtml(section.title)}</h2>
            <table>
                ${thead}
                ${tbody}
                ${tfoot}
            </table>
        </section>
    `;
}

export function exportReportToCsv(filename, sections) {
    const csv = sections
        .flatMap((section, index) => {
            const rows = buildSectionRows(section).map((row) => row.map(csvCell).join(","));
            if (index < sections.length - 1) {
                rows.push("");
            }
            return rows;
        })
        .join("\n");

    downloadBlob(new Blob([csv], {type: "text/csv;charset=utf-8;"}), filename);
}

export function exportReportToXlsx(filename, sections) {
    const workbook = XLSX.utils.book_new();

    sections.forEach((section, index) => {
        const sheetName = (section.sheetName || section.title || `Sheet ${index + 1}`).slice(0, 31);
        const worksheet = XLSX.utils.aoa_to_sheet(buildSectionRows(section));
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    XLSX.writeFile(workbook, filename);
}

export function openReportPrintWindow(title, sections) {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
        return;
    }

    const sectionsHtml = sections.map(buildPrintableTable).join("");
    printWindow.document.write(`
        <html>
            <head>
                <title>${escapeHtml(title)}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 24px;
                        color: #111827;
                    }
                    h1 {
                        margin-bottom: 20px;
                    }
                    h2 {
                        margin: 24px 0 10px;
                        font-size: 18px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 20px;
                    }
                    th, td {
                        border: 1px solid #d1d5db;
                        padding: 8px;
                        text-align: left;
                        vertical-align: top;
                    }
                    thead th, tfoot th {
                        background: #f3f4f6;
                    }
                </style>
            </head>
            <body>
                <h1>${escapeHtml(title)}</h1>
                ${sectionsHtml}
            </body>
        </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };
    printWindow.onafterprint = () => {
        printWindow.close();
    };
}
